import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, Info } from 'lucide-react';

const MaterialDetail = () => {
  const { slug } = useParams();
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMaterial = async () => {
      try {
        setLoading(true);
        // Using the by-slug endpoint for localized collections
        const response = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`);
        if (!response.ok) {
          throw new Error('Materialet blev ikke fundet');
        }
        const data = await response.json();
        setMaterial(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMaterial();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 pt-32 md:pt-48">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error || !material) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-50 pt-32 md:pt-48 px-4 text-center">
        <h1 className="text-2xl font-bold text-zinc-800 mb-4">Ups! Noget gik galt.</h1>
        <p className="text-zinc-600 mb-8">{error || 'Materialet kunne ikke findes.'}</p>
        <Link to="/materialer" className="bg-black text-white px-6 py-3 rounded-none font-bold hover:bg-zinc-800 transition-colors">
          Se alle materialer
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pt-32 md:pt-48 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Content Section */}
        <div className="max-w-3xl mx-auto space-y-12 mb-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-black text-zinc-900 mb-6 leading-tight uppercase tracking-tighter">
              {material.name.da}
            </h1>
            <p className="text-zinc-500 text-sm font-bold uppercase tracking-widest">{material.category}</p>
          </div>

          <div 
            className="prose prose-zinc prose-lg max-w-none text-zinc-600 leading-relaxed text-center"
            dangerouslySetInnerHTML={{ __html: material.description.da }}
          />

          {/* Dedicated Pricing Section */}
          <div className="bg-zinc-100/50 p-6 sm:p-8 border border-zinc-200/60">
            <h3 className="text-xl font-black text-black uppercase tracking-tight mb-6 text-center">Priser ved afhentning (inkl. moms)</h3>
            {material.price_per_unit ? (
              <div className="bg-white p-6 border border-zinc-100 text-center shadow-sm">
                <span className="block text-zinc-500 text-xs font-bold uppercase tracking-wider mb-2">Stykpris</span>
                <span className="text-4xl font-black text-black">{material.price_per_unit},- kr.</span>
                <span className="block text-zinc-400 text-xs mt-1">Sælges stykvis</span>
              </div>
            ) : material.category === 'Flis' ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white p-6 border border-zinc-100 text-center shadow-sm flex flex-col justify-between">
                  <div>
                    <span className="block text-zinc-500 text-xs font-bold uppercase tracking-wider mb-1">Murerbalje</span>
                    <span className="block text-zinc-400 text-xs mb-4">90 Liter</span>
                  </div>
                  <div>
                    <span className="text-3xl font-black text-black">{material.price_murerbalje},- kr.</span>
                  </div>
                </div>
                <div className="bg-white p-6 border border-zinc-100 text-center shadow-sm flex flex-col justify-between">
                  <div>
                    <span className="block text-zinc-500 text-xs font-bold uppercase tracking-wider mb-1">Trailer (1 aksel)</span>
                    <span className="block text-zinc-400 text-xs mb-4">Lille trailer</span>
                  </div>
                  <div>
                    <span className="text-3xl font-black text-black">{material.price_trailer_small},- kr.</span>
                  </div>
                </div>
                <div className="bg-white p-6 border border-zinc-100 text-center shadow-sm flex flex-col justify-between">
                  <div>
                    <span className="block text-zinc-500 text-xs font-bold uppercase tracking-wider mb-1">Trailer (2 aksler)</span>
                    <span className="block text-zinc-400 text-xs mb-4">Stor trailer</span>
                  </div>
                  <div>
                    <span className="text-3xl font-black text-black">{material.price_trailer_large},- kr.</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white p-6 border border-zinc-100 text-center shadow-sm flex flex-col justify-between">
                  <div>
                    <span className="block text-zinc-500 text-xs font-bold uppercase tracking-wider mb-1">Murerbalje</span>
                    <span className="block text-zinc-400 text-xs mb-4">90 Liter</span>
                  </div>
                  <div>
                    <span className="text-3xl font-black text-black">{material.price_murerbalje},- kr.</span>
                  </div>
                </div>
                <div className="bg-white p-6 border border-zinc-100 text-center shadow-sm flex flex-col justify-between">
                  <div>
                    <span className="block text-zinc-500 text-xs font-bold uppercase tracking-wider mb-1">Lille trailer</span>
                    <span className="block text-zinc-400 text-xs mb-4">300 - 750 kg</span>
                  </div>
                  <div>
                    <span className="text-3xl font-black text-black">{material.price_trailer_small},- kr.</span>
                  </div>
                </div>
                <div className="bg-white p-6 border border-zinc-100 text-center shadow-sm flex flex-col justify-between">
                  <div>
                    <span className="block text-zinc-500 text-xs font-bold uppercase tracking-wider mb-1">Stor trailer</span>
                    <span className="block text-zinc-400 text-xs mb-4">751 - 1.300 kg</span>
                  </div>
                  <div>
                    <span className="text-3xl font-black text-black">{material.price_trailer_large},- kr.</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-8 rounded-none border border-zinc-100 shadow-sm space-y-4">
            <div className="flex items-start gap-4 text-left">
              <div className="text-zinc-900 shrink-0">
                <Info size={28} />
              </div>
              <div>
                <h3 className="font-bold text-zinc-900 mb-1 text-xl">Bestilling & Levering</h3>
                <p className="text-zinc-600 text-lg">
                  Vi leverer direkte til din adresse på hele Djursland. Kontakt os for et uforpligtende tilbud på netop din opgave eller kig forbi en af vores pladser.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <a 
              href="tel:+4520181271" 
              className="flex-1 flex items-center justify-center gap-3 bg-black text-white px-8 py-6 rounded-none font-black text-xl hover:bg-zinc-800 transition-all shadow-xl shadow-black/20 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Phone size={24} />
              Bestil nu
            </a>
            <Link 
              to="/#priser" 
              className="flex-1 flex items-center justify-center bg-transparent text-zinc-900 border-2 border-zinc-900 px-8 py-6 rounded-none font-black text-xl hover:bg-zinc-50 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              Se alle priser
            </Link>
          </div>
        </div>

        {/* Image Gallery Section */}
        {material.image && material.image.length > 0 && (
          <div className="pt-16 border-t border-zinc-200">
            <h2 className="text-2xl font-black text-zinc-900 mb-10 uppercase tracking-tight text-center">Billedgalleri</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {material.image.slice(0, 3).map((img, index) => (
                <div 
                  key={index} 
                  className="aspect-square overflow-hidden bg-zinc-200 shadow-lg border border-white"
                >
                  <img 
                    src={img} 
                    alt={`${material.name.da} - billede ${index + 1}`} 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialDetail;
